import classnames from "classnames";
import Link from "next/link";
import { useState, useCallback, useEffect } from "react";

import formatPrice from "@/lib/formatPrice";
import routes from "@/lib/routes";
import formStyles from "@/styles/Form.module.scss";
import styles from "@/styles/Payment.module.scss";
import processStyles from "@/styles/Process.module.scss";

export default function RecommendArticleForm({
  recommendArticle = {},
  onChange,
}) {
  const { courses, products } = recommendArticle;
  // バリュープランのみの場合は表示しない
  if ((!courses || (courses.length === 1 && courses[0].valuePlan)) && !products)
    return null;
  // コース、商品が空の場合を別途判定する。どちらかがnull/undefinedの場合を考慮
  if ((courses?.length ?? 0) === 0 && (products?.length ?? 0) === 0)
    return null;

  return (
    <div className={processStyles.contentWrapper}>
      <div className={processStyles.content}>
        <div className={processStyles.contentHeader}>購入する商品</div>
        <div className={styles.container}>
          <div className={`${styles.section} ${styles.column}`}>
            {courses && courses.length === 1 && !courses[0].valuePlan && (
              <SingleCourse course={courses[0]} onChange={onChange} />
            )}
            {products && products.length === 1 && (
              <SingleProduct product={products[0]} onChange={onChange} />
            )}
            {products && products.length > 1 && (
              <MultipleProduct products={products} onChange={onChange} />
            )}
          </div>
        </div>
        <ul className={styles.attentionNote}>
          <li>
            ※特定商取引法について
            <Link
              href={routes.legalSctLaw}
              target="_blank"
              rel="noopener noreferrer"
              className={formStyles.textLink}
            >
              こちら
            </Link>
            からご確認ください。
          </li>
        </ul>
      </div>
    </div>
  );
}

function SingleCourse({ course, onChange }) {
  const coursePrice = formatPrice(course?.defaultPrice || 0);
  useEffect(() => {
    onChange({ type: "svod", id: [Number(course.id)] });
  }, [course, onChange]);

  return (
    <div className={styles.table}>
      <div className={styles.row}>
        <div className={styles.label}>商品名</div>
        <div className={styles.value}>{course.name}</div>
      </div>
      <div className={styles.row}>
        <div className={styles.label}>価格</div>
        <div className={styles.value}>
          <span className={styles.priceAmount}>
            <span>月額</span> {coursePrice}円<span>（税込）</span>
          </span>
        </div>
      </div>
    </div>
  );
}

function SingleProduct({ product, onChange }) {
  const productPrice = formatPrice(product.price || 0);
  useEffect(() => {
    onChange({ type: "tvod", id: [Number(product.id)] });
  }, [product, onChange]);

  return (
    <div className={styles.table}>
      <div className={styles.row}>
        <div className={styles.label}>商品名</div>
        <div className={styles.value}>{product.name}</div>
      </div>
      <div className={styles.row}>
        <div className={styles.label}>価格</div>
        <div className={styles.value}>
          <span className={styles.priceAmount}>
            {productPrice}円<span>（税込）</span>
          </span>
        </div>
      </div>
    </div>
  );
}

function MultipleProduct({ products, onChange }) {
  // 最初の1件をデフォルトで選択状態にする
  const [selected, setSelected] = useState(products[0].id);
  const handleChange = useCallback((e) => {
    setSelected(e.target.value);
  }, []);

  useEffect(() => {
    onChange({ type: "tvod", id: [Number(selected)] });
  }, [selected, onChange]);

  return (
    <div
      className={classnames("flex flexWrap", {
        flex50: products.length >= 2,
      })}
    >
      {products.map((product, i) => (
        <div key="product-wrapper-{i}">
          <div
            key={`prodict-${i}`}
            className={`${formStyles.inputField} ${formStyles.radio}`}
          >
            <input
              type="radio"
              name="product"
              id={`product-${i}`}
              className={formStyles.radioButton}
              value={product.id}
              checked={product.id === selected}
              onChange={handleChange}
            />
            <label htmlFor={`product-${i}`}>
              <div>
                <div className="textNowwrap">{product.name}</div>
                <div>
                  <span className={styles.priceAmount}>
                    価格 {formatPrice(product.price)}円<span>（税込）</span>
                  </span>
                </div>
              </div>
            </label>
          </div>
        </div>
      ))}
    </div>
  );
}
